import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import styled from 'styled-components';

import { Box, Switch, Switches } from '@/components/FancySwitch';
// import Text from '@/components/Text';
import useNetwork from '@/hooks/dq/useNetwork';
import useQueueSettings from '@/hooks/dq/useSettings';
import useAlerts from '@/hooks/useAlerts';
import useToasts from '@/hooks/useToasts';
import { EditorialQueuePage } from '@/types/resources';
import { api } from '@/utils/api';

// import { Color } from '@/utils/theme';
import EnablingModal from './EnablingModal';

const Wrap = styled(Box)`
  background: ${({ theme }) => theme.colors.white};
  padding: 3px 12px;
`;

const StyledSwitch = styled(Switch)`
  padding: 0 8px;
  width: auto;
`;

// const Dot = styled.div<{ $color: Color }>`
//   width: 6px;
//   height: 6px;
//   border-radius: 50%;
//   margin-right: 8px;
//   background: ${({ theme, $color }) => theme.colors[$color]};
// `;

// const SwitchLabel = styled(Text)`
//   display: flex;
//   align-items: center;
// `;

interface Props {
  activePage: EditorialQueuePage;
}

const QueueSwitch: FC<Props> = ({ activePage }) => {
  const alerts = useAlerts();
  const toasts = useToasts();
  const queryClient = useQueryClient();
  const [network] = useNetwork();

  const [showModal, setShowModal] = useState(false);

  const settings = useQueueSettings(activePage);

  const enabled = settings?.enabled ?? false;

  const onClick = async () => {
    if (!settings) {
      return alerts.error(
        'Frequency settings is not provided for this page/profile!',
        activePage.name,
      );
    }

    if (enabled) {
      try {
        const confirmed = await alerts.confirm(
          'Are you sure?',
          `Disable distribution queue for page ${activePage.name}`,
        );

        if (!confirmed) {
          return;
        }

        await api.post(`/v1/editorial/queue/status`, {
          network,
          page_id: activePage.id,
          enabled: false,
        });

        await queryClient.invalidateQueries([
          'editorial_distribution_queue_channels',
        ]);

        alerts.close();

        toasts.create('success', 'Queue disabled');
      } catch (_) {
        alerts.error();
      }

      return;
    }

    setShowModal(true);
  };

  return (
    <>
      <Wrap onClick={onClick} type="button">
        {/* <SwitchLabel $type="smallBold" as="div">
          <Dot $color={enabled ? 'success' : 'error'} />
          Queue:
        </SwitchLabel> */}
        <Switches $inactive={!enabled} $active={enabled} $disabled={false}>
          <StyledSwitch $type="smallHeadline">
            <span>Off</span>
          </StyledSwitch>
          <StyledSwitch $active={enabled} $type="smallHeadline">
            <span>On</span>
          </StyledSwitch>
        </Switches>
      </Wrap>
      <AnimatePresence>
        {showModal && (
          <EnablingModal
            onClose={() => setShowModal(false)}
            channelId={activePage.id}
            frequency={settings?.frequency!}
            network={network}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default QueueSwitch;
